import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import styled, { keyframes } from 'styled-components';

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 0.8; }
`;

const Container = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 5rem;

  @media (min-width: 1024px) {
    padding: 6rem 10% 4rem;
  }
`;

const HeroSection = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  gap: 3rem;

  @media (min-width: 768px) {
    grid-template-columns: 1.2fr 0.8fr;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`;

const Greeting = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 50%, #6366f1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (min-width: 1024px) {
    font-size: 3.5rem;
  }
`;

const Subtitle = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: #38bdf8;
  margin-bottom: 1.5rem;
  display: inline-block;
  padding: 4px 12px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.2);
  border-radius: 999px;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #94a3b8;
  line-height: 1.8;
  margin-bottom: 2.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PrimaryBtn = styled(Link)`
  display: inline-block;
  padding: 0.85rem 2rem;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  font-weight: 700;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.6);
  }
`;

const SecondaryBtn = styled(Link)`
  display: inline-block;
  padding: 0.85rem 2rem;
  background: transparent;
  color: #f8fafc;
  font-weight: 700;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const HeroVisual = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  animation: ${float} 6s ease-in-out infinite;

  @media (max-width: 768px) {
    order: -1;
    margin-bottom: 2rem;
  }
`;

const VisualCard = styled.div`
  width: 300px;
  height: 300px;
  border-radius: 30px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(6, 182, 212, 0.15) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.2) 0%, transparent 60%);
    animation: ${pulse} 8s infinite alternate;
  }
`;

const VisualCode = styled.div`
  font-family: 'Fira Code', 'Courier New', monospace;
  font-size: 0.9rem;
  color: #a5b4fc;
  z-index: 1;
  text-align: left;
  line-height: 1.6;

  span.tag { color: #f43f5e; }
  span.keyword { color: #38bdf8; }
  span.string { color: #34d399; }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #f8fafc;
  position: relative;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #06b6d4);
    border-radius: 99px;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const InfoCard = styled.div`
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(30, 41, 59, 0.7);
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-3px);
  }
`;

const CardIcon = styled.div`
  font-size: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 12px;
  color: #818cf8;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
`;

const CardValue = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
  color: #e2e8f0;
  word-break: break-all;
`;

const CardLink = styled.a`
  font-size: 0.95rem;
  font-weight: 600;
  color: #818cf8;
  transition: color 0.2s;

  &:hover {
    color: #a5b4fc;
    text-decoration: underline;
  }
`;

const EmptyHome = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 70px);
  color: #94a3b8;
`;

function Home() {
  const [dados, setDados] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function dadosHome() {
      try {
        const resposta = await api.get('/api/home');
        setDados(resposta.data || {});
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    dadosHome();
  }, []);

  if (loading) {
    return (
      <EmptyHome>
        <p>Carregando...</p>
      </EmptyHome>
    );
  }

  return (
    <Container>
      <HeroSection>
        <HeroContent>
          <Greeting>{dados.titulo || 'Olá, bem-vindo!'}</Greeting>
          <Subtitle>{dados.subtitulo || 'Desenvolvedor'}</Subtitle>
          <Description>
            {dados.descricao || 'Este é o meu portfólio profissional. Aqui você poderá encontrar minhas habilidades, histórico de carreira e principais projetos desenvolvidos.'}
          </Description>
          <ButtonGroup>
            <PrimaryBtn to="/projetos">Ver Projetos</PrimaryBtn>
            <SecondaryBtn to="/contato">Contato</SecondaryBtn>
          </ButtonGroup>
        </HeroContent>

        <HeroVisual>
          <VisualCard>
            <VisualCode>
              <p><span className="keyword">const</span> developer = &#123;</p>
              <p>&nbsp;&nbsp;name: <span className="string">"Danilo Kuwai"</span>,</p>
              <p>&nbsp;&nbsp;role: <span className="string">"Full Stack"</span>,</p>
              <p>&nbsp;&nbsp;skills: [<span className="string">"React"</span>, <span className="string">"Node"</span>],</p>
              <p>&nbsp;&nbsp;passionate: <span className="keyword">true</span></p>
              <p>&#125;;</p>
            </VisualCode>
          </VisualCard>
        </HeroVisual>
      </HeroSection>

      <InfoSection>
        <SectionTitle>Informações de Contato</SectionTitle>
        <InfoGrid>
          {dados.localizacao && (
            <InfoCard>
              <CardIcon>📍</CardIcon>
              <CardContent>
                <CardLabel>Localização</CardLabel>
                <CardValue>{dados.localizacao}</CardValue>
              </CardContent>
            </InfoCard>
          )}

          {dados.email && (
            <InfoCard>
              <CardIcon>✉️</CardIcon>
              <CardContent>
                <CardLabel>Email</CardLabel>
                <CardLink href={`mailto:${dados.email}`}>{dados.email}</CardLink>
              </CardContent>
            </InfoCard>
          )}

          {dados.telefone && (
            <InfoCard>
              <CardIcon>📞</CardIcon>
              <CardContent>
                <CardLabel>Telefone</CardLabel>
                <CardLink href={`tel:${dados.telefone.replace(/\D/g, '')}`}>{dados.telefone}</CardLink>
              </CardContent>
            </InfoCard>
          )}

          {dados.linkedin && (
            <InfoCard>
              <CardIcon>🔗</CardIcon>
              <CardContent>
                <CardLabel>LinkedIn</CardLabel>
                <CardLink href={dados.linkedin} target="_blank" rel="noopener noreferrer">Ver Perfil</CardLink>
              </CardContent>
            </InfoCard>
          )}

          {dados.github && (
            <InfoCard>
              <CardIcon>💻</CardIcon>
              <CardContent>
                <CardLabel>GitHub</CardLabel>
                <CardLink href={dados.github} target="_blank" rel="noopener noreferrer">Ver GitHub</CardLink>
              </CardContent>
            </InfoCard>
          )}
        </InfoGrid>
      </InfoSection>
    </Container>
  );
}

export default Home;
